import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { WhatsappLogo, Warning, CalendarX } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INSTRUCTOR_PHONE = '31620933773'; // +31 6 20933773 (Rijschool AutoPilot)

const REASONS = [
    { id: 'ziek', label: 'Ziek', emoji: '🤒' },
    { id: 'examen', label: 'Examen / Toets', emoji: '📝' },
    { id: 'school', label: 'School', emoji: '🎓' },
    { id: 'werk', label: 'Werk', emoji: '💼' },
    { id: 'anders', label: 'Anders, namelijk...', emoji: '💬' },
];

export default function CancelLessonModal({ lesson, studentName, onClose }) {
    const [selectedReason, setSelectedReason] = useState('');
    const [customReason, setCustomReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const isValid = selectedReason && (selectedReason !== 'anders' || customReason.trim().length >= 3);

    const reasonText = () => {
        if (selectedReason === 'anders') return customReason.trim();
        const r = REASONS.find(r => r.id === selectedReason);
        return r ? r.label : '';
    };

    const buildMessage = () => {
        const reason = reasonText();
        const lessonInfo = lesson
            ? `📅 Les van ${lesson.date}${lesson.time ? ` om ${lesson.time}` : ''} (${lesson.duration_minutes} min)`
            : '📅 Mijn geplande rijles';
        return `Hallo,\n\nIk wil helaas mijn rijles annuleren.\n\n👤 Naam: ${studentName}\n${lessonInfo}\n📌 Reden: ${reason}\n\nMijn excuses voor het ongemak. Ik hoor graag van u wanneer we een nieuwe afspraak kunnen inplannen.\n\nMet vriendelijke groet,\n${studentName}`;
    };

    const handleSend = () => {
        if (!isValid || submitting) return;
        setSubmitting(true);

        // Open WhatsApp immediately (synchronously on user click) so browsers don't block it
        const whatsappUrl = `https://wa.me/${INSTRUCTOR_PHONE}?text=${encodeURIComponent(buildMessage())}`;
        const win = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
        // Fallback: if popup was blocked, navigate current tab
        if (!win || win.closed || typeof win.closed === 'undefined') {
            window.location.href = whatsappUrl;
        }

        // Fire-and-forget the cancellation log (don't block the WhatsApp open)
        axios.post(`${API}/cancellations`, {
            scheduled_lesson_id: lesson?.id || null,
            lesson_date: lesson?.date || null,
            lesson_time: lesson?.time || null,
            lesson_duration: lesson?.duration_minutes || null,
            reason: reasonText(),
            reason_type: selectedReason,
        }, { withCredentials: true }).catch(e => console.error('Cancellation log failed:', e));

        setSubmitting(false);
        onClose();
    };

    // Check if within 24h window
    const within24h = (() => {
        if (!lesson?.date) return false;
        const lessonDt = new Date(`${lesson.date}T${lesson.time || '09:00'}`);
        const hoursUntil = (lessonDt - new Date()) / (1000 * 60 * 60);
        return hoursUntil < 24 && hoursUntil >= 0;
    })();

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="bg-[#2D3748] border-[#4A5568] text-white max-w-lg" data-testid="cancel-lesson-modal">
                <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <CalendarX size={22} className="text-[#D4A537]" weight="fill" />
                        Les annuleren
                    </DialogTitle>
                    <DialogDescription className="text-gray-400">
                        Selecteer de reden van je verhindering. Je bericht wordt via WhatsApp naar je rijinstructeur gestuurd.
                    </DialogDescription>
                </DialogHeader>

                {within24h && (
                    <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 flex items-start gap-2" data-testid="cancel-24h-warning">
                        <Warning size={20} className="text-red-400 flex-shrink-0 mt-0.5" weight="fill" />
                        <div className="text-sm text-red-300">
                            <strong>Let op:</strong> Je annuleert binnen 24 uur voor de les. Deze wordt daarom meegerekend als een gereden les volgens je lespakket.
                        </div>
                    </div>
                )}

                <div className="space-y-2">
                    <Label className="text-gray-300 text-sm">Reden van verhindering</Label>
                    <div className="grid grid-cols-1 gap-2">
                        {REASONS.map(r => (
                            <button
                                key={r.id}
                                type="button"
                                onClick={() => setSelectedReason(r.id)}
                                data-testid={`reason-${r.id}`}
                                className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-all ${
                                    selectedReason === r.id
                                        ? 'bg-[#D4A537]/15 border-[#D4A537] text-white'
                                        : 'bg-[#1A202C] border-[#4A5568] text-gray-300 hover:border-[#D4A537]/50'
                                }`}
                            >
                                <span className="text-xl">{r.emoji}</span>
                                <span className="font-medium">{r.label}</span>
                                {selectedReason === r.id && (
                                    <span className="ml-auto w-2 h-2 rounded-full bg-[#D4A537]" />
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {selectedReason === 'anders' && (
                    <div className="space-y-2">
                        <Label className="text-gray-300 text-sm">Beschrijf je reden</Label>
                        <Textarea
                            value={customReason}
                            onChange={(e) => setCustomReason(e.target.value)}
                            placeholder="Bijv. familieomstandigheden..."
                            className="bg-[#1A202C] border-[#4A5568] text-white min-h-[80px]"
                            data-testid="custom-reason-input"
                        />
                        {customReason.trim().length > 0 && customReason.trim().length < 3 && (
                            <p className="text-xs text-red-400">Voer minimaal 3 tekens in.</p>
                        )}
                    </div>
                )}

                <div className="flex gap-2 justify-end pt-2">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        className="border-[#4A5568] text-gray-300 hover:bg-[#3D4A5C]"
                        data-testid="cancel-modal-close"
                    >
                        Terug
                    </Button>
                    <Button
                        onClick={handleSend}
                        disabled={!isValid || submitting}
                        className="bg-green-600 hover:bg-green-500 text-white font-semibold disabled:opacity-50"
                        data-testid="send-cancel-whatsapp"
                    >
                        <WhatsappLogo size={18} className="mr-2" weight="fill" />
                        {submitting ? 'Versturen...' : 'Stuur via WhatsApp'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
